"use client";

import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Typed from "typed.js";

import { profileData } from "@/data";
import { Spotlight } from "./ui/Spotlight";

const GridGlobe = dynamic(() => import("./ui/GridGlobe"), {
  ssr: false,
  loading: () => (
    <div className="h-full w-full rounded-[23px] bg-black/40 animate-pulse" />
  ),
});

const textStagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

const textItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const Hero = () => {
  const typedRef = useRef<HTMLSpanElement | null>(null);
  const [showGlobe, setShowGlobe] = useState(false);

  useEffect(() => {
    // Defer globe loading to after page renders
    const timer = setTimeout(() => setShowGlobe(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!typedRef.current) return;

    const typed = new Typed(typedRef.current, {
      strings: ["Full Stack Developer", "App Developer", "Designer"],
      typeSpeed: 60,
      backSpeed: 35,
      backDelay: 1100,
      startDelay: 300,
      loop: true,
      showCursor: true,
      cursorChar: "|",
    });

    return () => {
      typed.destroy();
    };
  }, []);

  return (
    <section
      id="home"
      className="relative overflow-hidden min-h-[100svh] w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] pt-16 pb-12 sm:pt-28 md:pt-32 sm:pb-16 flex items-center"
    >
      <div className="pointer-events-none absolute inset-0 bg-grid-white/[0.04] [mask-image:radial-gradient(ellipse_at_center,white_25%,transparent_75%)]" />
      <div
        className="pointer-events-none absolute -top-24 -left-12 h-72 w-72 rounded-full bg-blue-500/40"
        style={{ opacity: 0.3, filter: "blur(80px)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-10 right-0 h-80 w-80 rounded-full bg-purple/40"
        style={{ opacity: 0.3, filter: "blur(80px)" }}
      />

      <div>
        <Spotlight
          className="-top-40 -left-10 sm:-left-16 sm:-top-32 md:-left-32 md:-top-20 h-screen"
          fill="white"
        />
        <Spotlight
          className="h-[60vh] sm:h-[80vh] w-[80vw] sm:w-[50vw] top-10 left-full"
          fill="purple"
        />
        <Spotlight
          className="left-20 sm:left-40 md:left-80 top-12 sm:top-16 md:top-28 h-[60vh] sm:h-[80vh] w-[80vw] sm:w-[50vw]"
          fill="blue"
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1180px] px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 sm:gap-8 lg:gap-10 items-center">
        <motion.div
          className="flex flex-col items-start text-left max-lg:items-center max-lg:text-center"
          variants={textStagger}
          initial="hidden"
          animate={{ opacity: 1 }}
          whileInView="show"
          viewport={{ once: true }}
        >
          <motion.div
            variants={textItem}
            className="inline-flex items-center rounded-full border border-white/20 bg-black/35 px-4 py-2 text-xs sm:text-sm text-white-200 backdrop-blur-md"
          >
            Open to freelance and product collaborations
          </motion.div>

          <motion.h1
            variants={textItem}
            className="mt-6 text-[42px] sm:text-[48px] lg:text-[56px] font-bold text-white leading-[1.1]"
          >
            {profileData.name}
          </motion.h1>
          <motion.h2
            variants={textItem}
            className="mt-3 min-h-[2rem] md:min-h-[2.5rem] text-[20px] text-purple font-semibold whitespace-nowrap"
          >
            <span ref={typedRef} />
          </motion.h2>

          <motion.p
            variants={textItem}
            className="mt-6 text-white-100 text-[16px] leading-[1.6] max-w-[520px]"
          >
            {profileData.heroHeadline}
          </motion.p>

          <motion.p
            variants={textItem}
            className="mt-6 uppercase tracking-[0.24em] text-blue-100 text-[14px]"
          >
            {profileData.heroTagline}
          </motion.p>

          <motion.div
            variants={textItem}
            className="mt-6 flex flex-wrap gap-4 w-full max-w-[520px]"
          >
            <a
              href="#projects"
              className="interactive-lift button-glow max-sm:flex-1 max-sm:text-center px-6 py-3 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-[0_12px_35px_rgba(99,102,241,0.35)] hover:opacity-95 transition"
            >
              View Work
            </a>
            <a
              href="#contact"
              className="interactive-lift max-sm:flex-1 max-sm:text-center px-6 py-3 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition"
            >
              Contact
            </a>
          </motion.div>

          <motion.div
            variants={textItem}
            className="mt-[18px] grid grid-cols-2 sm:grid-cols-3 gap-3 w-full max-w-[520px]"
          >
            {["Web Apps", "Mobile Apps", "UI Motion"].map((tag) => (
              <span
                key={tag}
                className={`interactive-lift rounded-full border border-white/15 bg-black/35 px-3 py-1.5 text-[13px] text-white-200 text-center ${
                  tag === "UI Motion" ? "max-sm:hidden" : ""
                }`}
              >
                {tag}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="relative w-full max-w-[460px] h-[320px] sm:h-[380px] lg:h-[430px] mx-auto lg:mx-0 lg:justify-self-start"
          initial={{ opacity: 0, x: 24, scale: 0.98 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
        >
          <motion.div
            className="absolute inset-0"
            animate={{ y: [0, -10, 0], rotateZ: [0, 0.4, 0] }}
            transition={{
              duration: 6,
              ease: "easeInOut",
              repeat: Infinity,
              type: "tween",
            }}
          >
            <div className="absolute inset-0 rounded-[24px] border border-white/15 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm" />
            <div className="absolute inset-[1px] rounded-[23px] overflow-hidden">
              {showGlobe ? (
                <GridGlobe
                  wrapperClassName="flex items-center justify-center w-full h-full"
                  contentClassName="w-full h-full relative overflow-hidden"
                  worldClassName="absolute inset-0 z-10"
                  showBottomGradient={false}
                />
              ) : (
                <div className="h-full w-full bg-black/40 animate-pulse" />
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.4 }}
            className="absolute bottom-4 left-4 rounded-xl border border-white/15 bg-black/55 px-4 py-2 backdrop-blur-md"
          >
            <p className="text-xs text-white-200">Based in</p>
            <p className="text-sm text-white font-semibold">
              {profileData.location}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.4 }}
            className="absolute top-4 right-4 rounded-xl border border-white/15 bg-black/55 px-4 py-2 backdrop-blur-md max-sm:hidden"
          >
            <p className="text-xs text-white-200">Focus</p>
            <p className="text-sm text-white font-semibold">
              Fast + Scalable Builds
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
